// File: src/components/lms/Cards/CourseCard/CourseCard.tsx

import React from "react";
import styled from "styled-components";
import { Typography } from "../../../general/Typography";
import { Flex } from "../../../layouts/wrappers/Flex";
import { ProgressBar } from "../../Bars/ProgressBar";
import { XPBadge } from "../../Gamification/XPBadge";
import { LevelBadge } from "../../Gamification/LevelBadge";
import { TagPill } from "../../../general/TagPill";
import { Button } from "../../../general/Button";
import { Heart } from "lucide-react";

export interface CourseCardProps {
  id: string;
  title: string;
  instructor: string;
  imageUrl: string;
  tags?: string[];
  progress: number; // 0-100
  xp?: number;
  level?: number;
  completed?: boolean;
  reactions?: number;
  onClick?: () => void;
  cta?: "Start" | "Resume" | "Preview" | "Completed";
}

const Card = styled.button`
  width: 100%;
  border: none;
  padding: 0;
  background: none;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radii.lg};
  box-shadow: ${({ theme }) => theme.elevations.low};
  overflow: hidden;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.01);
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background-size: cover;
  background-position: center;
  position: relative;
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const CardBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

export const CourseCard: React.FC<CourseCardProps> = ({
  title,
  instructor,
  imageUrl,
  tags = [],
  progress,
  xp,
  level,
  completed = false,
  reactions,
  onClick,
  cta = "Start",
}) => {
  return (
    <Card onClick={onClick}>
      <ImageWrapper style={{ backgroundImage: `url(${imageUrl})` }}>
      <Overlay>
        <Flex gap="xs" wrap="wrap">
          {tags.map((tag) => (
            <TagPill key={tag} label={tag} />
          ))}
        </Flex>
        <Flex justify="space-between" align="flex-end">
          {xp && <XPBadge xp={xp} />}
          {level !== undefined && <LevelBadge level={level} />}
        </Flex>
      </Overlay>
      </ImageWrapper>

      <CardBody>
        <Flex direction="column" gap="xs">
          <Typography variant="heading2">{title}</Typography>
          <Typography variant="caption" className="text-muted">
            By {instructor}
          </Typography>
          <ProgressBar progress={progress} />

          <Flex justify="space-between" align="center">
            <Button size="small" variant="primary">
              {completed ? "Completed" : cta}
            </Button>
            {typeof reactions === "number" && (
              <Flex gap="xs" align="center">
                <Heart size={16} />
                <Typography variant="caption">{reactions}</Typography>
              </Flex>
            )}
          </Flex>
        </Flex>
      </CardBody>
    </Card>
  );
};
